import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  Autocomplete,
  TextField,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SuperAdminService, { FiscalDeviceCreateDto, FiscalDeviceWithCompany } from '../../services/SuperAdminService';
import FiscalDeviceFormFields, { emptyFiscalDeviceForm, fiscalDeviceToForm } from './FiscalDeviceFormFields';

interface CompanyOption {
  id: string;
  name: string;
}

interface FiscalDeviceFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Kayıt başarılı olduğunda listeyi tazelemek için. */
  onSaved: () => void;
  /** Düzenlenecek cihaz; null ise yeni kayıt. */
  device: FiscalDeviceWithCompany | null;
  companies: CompanyOption[];
  /** Yeni kayıtta ön seçili şirket (sekmedeki şirket filtresi). */
  defaultCompanyId?: string | null;
}

/**
 * Süperadmin "Yazarkasalar" sekmesinden cihaz ekleme/düzenleme.
 *
 * Şirket kartından açılan `FiscalDeviceModal` tek bir şirketin cihazlarını
 * yönetir ve şirket zaten bellidir. Burada tüm şirketlerin listesi göründüğü
 * için yeni kayıtta şirket seçimi isteniyor; düzenlemede cihazın şirketi
 * değiştirilemez (backend güncellemede CompanyId eşleşmesi arıyor).
 */
const FiscalDeviceFormDialog: React.FC<FiscalDeviceFormDialogProps> = ({
  open,
  onClose,
  onSaved,
  device,
  companies,
  defaultCompanyId,
}) => {
  const [form, setForm] = useState<FiscalDeviceCreateDto>(emptyFiscalDeviceForm);
  const [lineOwnerOptions, setLineOwnerOptions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(device ? fiscalDeviceToForm(device) : { ...emptyFiscalDeviceForm, companyId: defaultCompanyId || '' });
  }, [open, device, defaultCompanyId]);

  useEffect(() => {
    if (!open) return;
    SuperAdminService.getInstance()
      .getFiscalDeviceLineOwners()
      .then(setLineOwnerOptions);
  }, [open]);

  const handleChange = (field: keyof FiscalDeviceCreateDto, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const selectedCompany = companies.find(c => c.id === form.companyId) || null;

  const handleSave = async () => {
    if (!form.companyId) {
      setError('Şirket seçmeden cihaz kaydedilemez.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const service = SuperAdminService.getInstance();
      const payload = {
        ...form,
        activationDate: form.activationDate || null,
        expiryDate: form.expiryDate || null,
      };

      if (device) {
        await service.updateFiscalDevice(device.id, payload);
      } else {
        await service.addFiscalDevice(payload);
      }

      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Box>
          <Typography variant="h6" component="span">
            {device ? 'Yazarkasa Cihazı Düzenle' : 'Yeni Yazarkasa Cihazı'}
          </Typography>
          {device && (
            <Typography variant="body2" component="div" sx={{ opacity: 0.9 }}>
              {device.companyName || selectedCompany?.name || device.companyId}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} disabled={saving} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      {/* MUI, DialogTitle'dan sonra gelen DialogContent'in üst boşluğunu sıfırlıyor;
          outlined input'ların üstteki label'ı başlık bandının altında kalmasın diye geri veriyoruz. */}
      <DialogContent sx={{ pt: '24px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {device ? (
          <TextField
            size="small"
            label="Şirket"
            value={device.companyName || selectedCompany?.name || device.companyId}
            disabled
            fullWidth
            helperText="Cihazın şirketi değiştirilemez."
            sx={{ mb: 2, maxWidth: 420 }}
          />
        ) : (
          <Autocomplete
            size="small"
            options={companies}
            getOptionLabel={opt => opt.name}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={selectedCompany}
            onChange={(_, v) => handleChange('companyId', v?.id || '')}
            renderInput={params => (
              <TextField {...params} label="Şirket" placeholder="Cihazın ekleneceği şirket" required />
            )}
            sx={{ mb: 2, maxWidth: 420 }}
          />
        )}

        <Divider sx={{ mb: 2 }} />

        <FiscalDeviceFormFields form={form} onChange={handleChange} lineOwnerOptions={lineOwnerOptions} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" startIcon={<CancelIcon />} onClick={onClose} disabled={saving}>İptal</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? 'Kaydediliyor...' : device ? 'Güncelle' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiscalDeviceFormDialog;
